import axios, { AxiosError } from 'axios';
import { serviceUrls } from '../config/serviceUrls';
import type { GrammarApiResponse } from '../types/grammar';

export class GrammarServiceError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'GrammarServiceError';
    this.code = code;
    this.status = status;
  }
}

const grammarClient = axios.create({
  baseURL: serviceUrls.grammarServiceUrl,
  withCredentials: false,
  timeout: 60_000,
});

const normalizeResponse = (data: unknown): GrammarApiResponse => {
  const safe = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;
  const suggestions = Array.isArray(safe.suggestions) ? safe.suggestions : [];
  const rubric = (typeof safe.rubric === 'object' && safe.rubric !== null
    ? safe.rubric
    : {}) as GrammarApiResponse['rubric'];

  return {
    text: typeof safe.text === 'string' ? safe.text : '',
    suggestions: suggestions as GrammarApiResponse['suggestions'],
    rubric,
  };
};

const toGrammarError = (error: unknown): never => {
  if (error instanceof AxiosError) {
    if (error.response?.status === 429) {
      throw new GrammarServiceError('AI busy right now. Please try again shortly.', 'QUOTA_EXHAUSTED', 429);
    }

    const apiMessage =
      (error.response?.data as { message?: string } | undefined)?.message ||
      error.message ||
      'Grammar service request failed.';

    throw new GrammarServiceError(apiMessage, undefined, error.response?.status);
  }

  if (error instanceof Error) {
    throw new GrammarServiceError(error.message);
  }

  throw new GrammarServiceError('Unexpected grammar service error.');
};

/** OCR proofread only. Normal grammar check uses gateway wrapper via learning API (checkWriting). */
export async function ocrProofreadFile(file: File): Promise<GrammarApiResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await grammarClient.post('/api/ocr-proofread', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return normalizeResponse(response.data);
  } catch (error) {
    return toGrammarError(error);
  }
}

export type OcrExtractTextResponse = {
  text: string;
};

export async function ocrExtractTextFile(file: File): Promise<OcrExtractTextResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await grammarClient.post('/api/ocr-extract-text', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Safely read response.data.text
    let text = String((response.data && (response.data as any).text) ?? '');

    // If backend wraps the whole OCR text in outer quotes, strip one outer pair.
    if (text.startsWith('"') && text.endsWith('"')) {
      text = text.slice(1, -1);
    }

    return { text };
  } catch (error) {
    return toGrammarError(error);
  }
}
